import React from 'react';
import { format } from 'date-fns';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import {
    AdminLeaderboardMemberPreviewResponse,
    AdminTeamLeaderboardPreviewResponse,
    AggregationAlgorithm,
    LeaderboardMetric,
    TeamSupportDto
} from '@api/ChallengeClient';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { MemberRow, PreviewForm, TeamRow } from './types';

function today(): string {
    return new Date().toISOString().slice(0, 10);
}

export const DEFAULT_FORM: PreviewForm = {
    type: 'COMMUNITY_MEMBERS',
    leaderboardMetric: LeaderboardMetric.ACTIVITY_POINTS,
    dateFrom: today(),
    dateTo: today(),
    aggregation: AggregationAlgorithm.SUM
};

const EMPTY_MEMBER_PAGE = {
    content: [] as MemberRow[],
    number: 0,
    size: 0,
    totalElements: 0
};

const EMPTY_TEAM_PAGE = {
    content: [] as TeamRow[],
    number: 0,
    size: 0,
    totalElements: 0
};

function buildQuery(
    form: PreviewForm,
    page: number,
    size: number,
    includeEmails = false
) {
    return {
        leaderboardMetric: form.leaderboardMetric,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        aggregation:
            form.type === 'COMMUNITY_TEAMS' ? form.aggregation : undefined,
        workoutCategory: form.workoutCategory,
        workoutType: form.workoutType,
        currencyCode: form.currencyCode || undefined,
        compositePointTypeKey: form.compositePointTypeKey || undefined,
        includeEmails: includeEmails || undefined,
        page,
        size
    };
}

function toMemberPage(data: AdminLeaderboardMemberPreviewResponse) {
    return {
        content: (data.content || []).map((entry) => ({
            ...entry,
            id: entry.memberId
        })),
        number: data.page,
        size: data.size,
        totalElements: data.totalElements
    };
}

function toTeamPage(data: AdminTeamLeaderboardPreviewResponse) {
    const content = (data.content || []).map((entry) => ({
        ...entry,
        id: entry.teamId
    }));
    return {
        content,
        number: data.page,
        size: data.size,
        // Team aggregate is not totalled server-side; show the returned page as-is.
        totalElements: content.length
    };
}

export type ExportFormat = 'csv' | 'json';

const EXPORT_PAGE_SIZE = 1000;
const EXPORT_TEAM_SIZE = 10000;

function csvCell(value: string): string {
    return /["\n\r,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCsv(headers: string[], rows: string[][]): string {
    return [headers, ...rows]
        .map((row) => row.map(csvCell).join(','))
        .join('\r\n');
}

function memberExport(rows: MemberRow[], exportFormat: ExportFormat): string {
    const data = rows.map((row) => ({
        first_name: row.firstName,
        last_name: row.lastName,
        user_id: row.userId,
        emails: row.emails,
        place: row.position,
        score: row.points
    }));

    if (exportFormat === 'json') {
        return JSON.stringify(data, null, 2);
    }

    return toCsv(
        ['first_name', 'last_name', 'user_id', 'emails', 'place', 'score'],
        data.map((row) => [
            row.first_name,
            row.last_name,
            row.user_id,
            row.emails.join(';'),
            String(row.place),
            String(row.score)
        ])
    );
}

function teamExport(rows: TeamRow[], exportFormat: ExportFormat): string {
    const data = rows.map((row) => ({
        place: row.position,
        team: row.name,
        members: row.memberCount,
        score: row.points
    }));

    if (exportFormat === 'json') {
        return JSON.stringify(data, null, 2);
    }

    return toCsv(
        ['place', 'team', 'members', 'score'],
        data.map((row) => [
            String(row.place),
            row.team,
            String(row.members),
            String(row.score)
        ])
    );
}

function downloadFile(
    content: string,
    communityId: number,
    exportFormat: ExportFormat
) {
    const blob = new Blob([content], {
        type: exportFormat === 'csv' ? 'text/csv' : 'application/json'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
        'download',
        `leaderboard-${communityId}-${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`
    );
    link.click();
    window.URL.revokeObjectURL(url);
}

export function useStore(communityId: number) {
    const { challengeClient } = injectTSDI(Api);
    const [size, setSize] = React.useState(100);
    const lastRun = React.useRef<PreviewForm | null>(null);

    const teams = useWrapRequest(
        async (): Promise<TeamSupportDto[]> => {
            const result =
                await challengeClient.support.teamSupportControllerGetPage({
                    communityId,
                    page: 0,
                    size: 1000
                });
            return result.data.content || [];
        },
        { defaultData: [] as TeamSupportDto[] }
    );

    const memberPreview = useWrapRequest(
        async (form: PreviewForm, page: number, pageSize: number) => {
            const query = buildQuery(form, page, pageSize);
            const result =
                form.type === 'TEAM_MEMBERS'
                    ? await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewTeamMembers(
                          form.teamId!,
                          query
                      )
                    : await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewCommunityMembers(
                          communityId,
                          query
                      );
            return toMemberPage(result.data);
        },
        { defaultData: EMPTY_MEMBER_PAGE }
    );

    const teamPreview = useWrapRequest(
        async (form: PreviewForm, page: number, pageSize: number) => {
            const result =
                await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewCommunityTeams(
                    communityId,
                    buildQuery(form, page, pageSize)
                );
            return toTeamPage(result.data);
        },
        { defaultData: EMPTY_TEAM_PAGE }
    );

    const run = React.useCallback(
        (form: PreviewForm) => {
            lastRun.current = form;
            if (form.type === 'COMMUNITY_TEAMS') {
                teamPreview.request([form, 0, size]);
            } else {
                memberPreview.request([form, 0, size]);
            }
        },
        [memberPreview, teamPreview, size]
    );

    const changePage = React.useCallback(
        (page: number) => {
            const form = lastRun.current;
            if (!form) {
                return;
            }
            if (form.type === 'COMMUNITY_TEAMS') {
                teamPreview.request([form, page, size]);
            } else {
                memberPreview.request([form, page, size]);
            }
        },
        [memberPreview, teamPreview, size]
    );

    const changePageSize = React.useCallback(
        (pageSize: number) => {
            setSize(pageSize);
            const form = lastRun.current;
            if (!form) {
                return;
            }
            if (form.type === 'COMMUNITY_TEAMS') {
                teamPreview.request([form, 0, pageSize]);
            } else {
                memberPreview.request([form, 0, pageSize]);
            }
        },
        [memberPreview, teamPreview]
    );

    const exportLeaderboard = React.useCallback(
        async (exportFormat: ExportFormat) => {
            const form = lastRun.current;
            if (!form) {
                return;
            }

            if (form.type === 'COMMUNITY_TEAMS') {
                const result =
                    await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewCommunityTeams(
                        communityId,
                        buildQuery(form, 0, EXPORT_TEAM_SIZE)
                    );
                downloadFile(
                    teamExport(toTeamPage(result.data).content, exportFormat),
                    communityId,
                    exportFormat
                );
                return;
            }

            const rows: MemberRow[] = [];
            let page = 0;
            let hasMore = true;
            while (hasMore) {
                const query = buildQuery(form, page, EXPORT_PAGE_SIZE, true);
                const result =
                    form.type === 'TEAM_MEMBERS'
                        ? await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewTeamMembers(
                              form.teamId!,
                              query
                          )
                        : await challengeClient.support.adminLeaderboardPreviewSupportControllerPreviewCommunityMembers(
                              communityId,
                              query
                          );
                const mapped = toMemberPage(result.data);
                rows.push(...mapped.content);
                hasMore =
                    mapped.content.length > 0 &&
                    rows.length < mapped.totalElements;
                page++;
            }

            downloadFile(memberExport(rows, exportFormat), communityId, exportFormat);
        },
        [challengeClient, communityId]
    );

    React.useEffect(() => {
        teams.request([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityId]);

    return {
        teams,
        memberPreview,
        teamPreview,
        run,
        changePage,
        changePageSize,
        exportLeaderboard
    };
}
